import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth.service';
import { CoreService, Menu, MenuAccessResponse } from '../../../services/core/core.service';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { ProductCatalogService } from '../../../services/product-catalog/product-catalog.service';
import { ProductSelectionService } from '../../../services/product-selection/product-selection.service';

interface SidebarMenu extends Menu {
  expanded?: boolean;
  submenu?: SidebarMenu[];
  productUrls?: Record<string, string>;
}

/** Producto disponible en el selector de la sección "Productos". */
interface SidebarProductOption {
  id: string;
  label: string;
  icon: string;
  modules: string[];
}

const PRODUCT_MODULES = ['devices', 'models', 'firmware', 'operation', 'support', 'help'] as const;
const PRODUCT_MODULE_META: Record<(typeof PRODUCT_MODULES)[number], { title: string; icon: string }> = {
  devices: { title: 'Dispositivos', icon: 'pi pi-box' },
  models: { title: 'Modelos', icon: 'pi pi-list' },
  firmware: { title: 'Firmware', icon: 'pi pi-code' },
  operation: { title: 'Operación', icon: 'pi pi-sliders-h' },
  support: { title: 'Tickets', icon: 'pi pi-ticket' },
  help: { title: 'Ayuda', icon: 'pi pi-info-circle' },
};

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: false,
})
export class SidebarComponent implements OnInit {
  /** `floating` muestra el panel desprendido de los bordes; `full` lo ancla a toda la altura. */
  @Input() variant: 'floating' | 'full' = 'floating';
  public menuItems: SidebarMenu[] = [];
  /** Productos habilitados para el usuario, según los menús que entrega la API. */
  public productOptions: SidebarProductOption[] = [];

  public activeRoute: string = '';

  /** Producto vigente del selector; se conserva aunque la vista actual no sea de productos. */
  public get selectedProductId(): string {
    return this.productSelection.selected;
  }

  constructor(
    private router: Router,
    private coreService: CoreService,
    private authService: AuthService,
    private productCatalogService: ProductCatalogService,
    private productSelection: ProductSelectionService,
    public sidebarService: SidebarService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.activeRoute = event.urlAfterRedirects;
      this.syncSelectedProduct();
      this.updateExpandedState();
      // Cerrar sidebar al navegar
      this.sidebarService.close();
    });
  }

  ngOnInit(): void {
    this.activeRoute = this.router.url;
    this.syncSelectedProduct();
    this.updateExpandedState();
    void this.loadMenuAccess();
  }

  private updateExpandedState(): void {
    this.menuItems.forEach((section) => {
      section.expanded = true;
      if (this.hasSubmenu(section)) this.expandSubmenus(section.submenu!);
    });
  }

  private expandSubmenus(items: SidebarMenu[]): void {
    items.forEach((item) => {
      if (this.hasSubmenu(item)) {
        item.expanded = true;
        this.expandSubmenus(item.submenu!);
      }
    });
  }

  private async loadMenuAccess(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.authService.getCurrentUser()?.accessToken) {
      return;
    }

    try {
      const menuAccess: MenuAccessResponse = await this.coreService.getMenuAccess();

      if (!menuAccess.configured) {
        this.menuItems = [];
        this.updateExpandedState();
        return;
      }

      this.menuItems = this.consolidateProductMenus(this.normalizeRemoteMenus(menuAccess.menus ?? []));
      this.updateExpandedState();
    } catch (error) {
      console.error('[SidebarComponent] Error al cargar acceso de menús:', error);
      this.menuItems = [];
      this.updateExpandedState();
    }
  }

  private normalizeRemoteMenus(items: Menu[]): SidebarMenu[] {
    return items
      .filter((item) => item && item.enabled !== false)
      .sort((left, right) => left.id - right.id)
      .map((item) => ({
        ...item,
        expanded: true,
        submenu: item.submenu ? this.normalizeRemoteMenus(item.submenu) : undefined,
      }));
  }

  private consolidateProductMenus(sections: SidebarMenu[]): SidebarMenu[] {
    return sections.map((section) => {
      if (!this.isProductSection(section)) return section;

      const urlsByModule = new Map<string, Record<string, string>>();
      this.collectProductModuleUrls(section.submenu ?? [], urlsByModule);
      this.productOptions = this.buildProductOptions(urlsByModule);
      this.syncSelectedProduct();
      const submenu = PRODUCT_MODULES.flatMap((moduleId, index) => {
        const productUrls = urlsByModule.get(moduleId);
        if (!productUrls || Object.keys(productUrls).length === 0) return [];
        const meta = PRODUCT_MODULE_META[moduleId];
        return [{ id: index + 1, title: meta.title, icon: meta.icon, url: Object.values(productUrls)[0], enabled: true, expanded: true, productUrls }];
      });

      return { ...section, expanded: true, submenu };
    });
  }

  private collectProductModuleUrls(items: SidebarMenu[], target: Map<string, Record<string, string>>): void {
    for (const item of items) {
      // Regex derivada de PRODUCT_MODULES para reconocer las URLs de módulos de producto
      // (incluye 'operation', exclusivo hoy de birdattack).
      const match = item.url?.match(
        /^\/home\/products\/([^/]+)\/(devices|models|firmware|operation|support|help)(?:\/|$)/,
      );
      if (match) {
        const [, productId, moduleId] = match;
        const urls = target.get(moduleId) ?? {};
        urls[productId] = `/home/products/${productId}/${moduleId}`;
        target.set(moduleId, urls);
      }
      if (item.submenu?.length) this.collectProductModuleUrls(item.submenu, target);
    }
  }

  /** La sección de productos aloja el selector, sobre sus módulos. */
  isProductSection(section: SidebarMenu): boolean {
    return section.title.trim().toLowerCase() === 'productos';
  }

  /**
   * Cambiar de producto mantiene el módulo actual cuando ese producto lo tiene;
   * si no, se abre el primer módulo disponible del producto elegido.
   */
  onProductChange(productId: string): void {
    if (!productId || productId === this.currentRouteProductId) return;
    const product = this.productOptions.find((option) => option.id === productId);
    if (!product) return;

    const currentModule = this.activeRoute.match(/^\/home\/products\/[^/]+\/([^/?#]+)/)?.[1];
    const targetModule = currentModule && product.modules.includes(currentModule) ? currentModule : product.modules[0];
    this.productSelection.select(productId);
    this.router.navigate(['/home/products', productId, ...(targetModule ? [targetModule] : [])]);
    this.closeSidebar();
  }

  private get currentRouteProductId(): string {
    return this.activeRoute.match(/^\/home\/products\/([^/?#]+)/)?.[1] ?? '';
  }

  /**
   * La ruta manda cuando apunta a un producto; fuera de esas rutas se respeta lo ya elegido y solo
   * se cae al primer producto disponible si no hay selección previa (o dejó de estar habilitada).
   */
  private syncSelectedProduct(): void {
    const routedProduct = this.currentRouteProductId;
    if (routedProduct && this.productOptions.some((option) => option.id === routedProduct)) {
      this.productSelection.select(routedProduct);
      return;
    }
    if (this.productOptions.length === 0) {
      return;
    }
    const current = this.productSelection.selected;
    if (!current || !this.productOptions.some((option) => option.id === current)) {
      this.productSelection.select(this.productOptions[0].id);
    }
  }

  private buildProductOptions(urlsByModule: Map<string, Record<string, string>>): SidebarProductOption[] {
    const modulesByProduct = new Map<string, string[]>();
    for (const moduleId of PRODUCT_MODULES) {
      const productUrls = urlsByModule.get(moduleId);
      if (!productUrls) continue;
      for (const productId of Object.keys(productUrls)) {
        modulesByProduct.set(productId, [...(modulesByProduct.get(productId) ?? []), moduleId]);
      }
    }

    return [...modulesByProduct.entries()]
      .map(([id, modules]) => ({
        id,
        label: this.productCatalogService.getDisplayName(id),
        icon: this.productCatalogService.getEntry(id)?.iconClass ?? 'pi pi-box',
        modules,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  navigationUrl(item: SidebarMenu): string {
    if (!item.productUrls) return item.url;
    // Dentro de una vista de producto, la URL visible es la fuente de verdad. Esto evita que una
    // selección persistida de otra sesión envíe un módulo compartido (por ejemplo Firmware) al
    // producto anterior. Fuera de Productos se conserva la selección del selector.
    return item.productUrls[this.currentRouteProductId] || item.productUrls[this.selectedProductId] || item.url;
  }

  isActive(item: SidebarMenu): boolean {
    const url = this.navigationUrl(item);
    if (!url) return false;
    return this.activeRoute.startsWith(url);
  }

  hasSubmenu(item: SidebarMenu): boolean {
    return Boolean(item.submenu && item.submenu.length > 0);
  }

  closeSidebar(): void {
    this.sidebarService.close();
  }

  /** El menú se recarga desde el backend; su título e URL identifican cada entrada de forma estable. */
  trackBySectionTitle(_index: number, section: { title: string }): string {
    return section.title;
  }

  trackByMenuUrl(_index: number, item: { url?: string; title?: string }): string {
    return item.url || item.title || '';
  }
}
